import { type FC, useState } from 'react';
import { Button, Card, DataTable } from '@components/UI';
import { DataLoadingSpinner } from '@/components/UI/DataLoadingSpinner.tsx';
import { formatTradeLogicV2ExitCandidateLabel } from '@/components/TradeLogic/formatTradeLogicV2ExitCandidateLabel';
import ColumnVisibilityControl from './ColumnVisibilityControl';
import FilterBacktestsBulkActions from './FilterBacktestsBulkActions';
import { columnRegistry } from '../backtestColumns';
import type { TradingStrategyBacktest } from '@/types/appRouter';
import type { ColumnDef } from '@/components/UI/dataTableConfig';

interface CombinationSummary {
    combination: {
        exitCandidateId: number | null;
        exitCandidateHash: string | null;
        optionBuyingStrategyIdLeg1: number | null;
        optionBuyingStrategyIdLeg2: number | null;
        optionBuyingStrategyIdLeg3: number | null;
        optionBuyingStrategyIdLeg4: number | null;
        optionBuyingStrategyIdLeg5: number | null;
        optionBuyingStrategyIdLeg6: number | null;
    };
    backtestIds: number[];
    avgProfit: number;
    avgPercentageGain: number;
    avgAvgTradeProfit: number;
    avgWinRate: number;
    avgSharpeRatio: number;
    avgMedianProfitPercent: number;
    avgProfitPerDayPercent: number;
    backtestCount: number;
}

interface FilterBacktestsResultsProps {
    isInitialLoading: boolean;
    isBacktestsLoading: boolean;
    backtests: TradingStrategyBacktest[];
    beforeDate: string | null;
    hasStrategyId: boolean;
    selectedStrategyId: string;
    selectedEntityType: string;
    visibleColumns: Set<string>;
    columnsWithSelection: ColumnDef<TradingStrategyBacktest>[];
    selectedBacktestIdsCount: number;
    onDeleteSelectedClick: () => void;
    onFilterDeletePreview: () => void;
    analysisCombinations: CombinationSummary[];
    isAnalysisLoading: boolean;
    onVisibilityChange: (columns: Set<string>) => void;
    analysisSummary: {
        totalBacktests: number;
        uniqueCombinations: number;
        uniqueExitCandidates: number;
        uniqueOptionStrategies: number;
    } | null;
    analysisByExitCandidate: {
        exitCandidateId: number | null;
        exitCandidateHash: string | null;
        avgProfit: number;
        avgPercentageGain: number;
        avgWinRate: number;
        avgMedianProfitPercent: number;
        avgProfitPerDayPercent: number;
        backtestCount: number;
    }[];
    analysisByOptionStrategy: {
        strategyId: number;
        avgProfit: number;
        avgPercentageGain: number;
        avgWinRate: number;
        avgMedianProfitPercent: number;
        avgProfitPerDayPercent: number;
        backtestCount: number;
        legsUsedIn: string[];
    }[];
}

const FilterBacktestsResults: FC<FilterBacktestsResultsProps> = (props) => {
    const {
        isInitialLoading,
        isBacktestsLoading,
        backtests,
        beforeDate,
        hasStrategyId,
        visibleColumns,
        columnsWithSelection,
        selectedBacktestIdsCount,
        onDeleteSelectedClick,
        onFilterDeletePreview,
        analysisCombinations,
        isAnalysisLoading,
        onVisibilityChange,
        analysisSummary,
        analysisByExitCandidate,
        analysisByOptionStrategy
    } = props;

    const [isAnalysisOpen, setIsAnalysisOpen] = useState(false);
    const [activeAnalysisTab, setActiveAnalysisTab] = useState<'combinations' | 'exit' | 'options'>('combinations');

    const combinationColumns: ColumnDef<CombinationSummary>[] = [
        {
            id: 'rank',
            header: 'Rank',
            size: 60,
            enableSorting: false,
            cell: (info) => info.row.index + 1
        },
        {
            id: 'optionStrategies',
            header: 'Option strategies',
            size: 180,
            accessorFn: (row) => {
                const ids = [
                    row.combination.optionBuyingStrategyIdLeg1,
                    row.combination.optionBuyingStrategyIdLeg2,
                    row.combination.optionBuyingStrategyIdLeg3,
                    row.combination.optionBuyingStrategyIdLeg4,
                    row.combination.optionBuyingStrategyIdLeg5,
                    row.combination.optionBuyingStrategyIdLeg6
                ].filter((id) => id !== null && id !== undefined);

                return ids.length ? ids.join(', ') : 'None';
            },
            cell: (info) => {
                const value = info.getValue() as string;

                if (value === 'None') {
                    return <span className="text-zinc-400 text-xs">None</span>;
                }

                return <span className="font-mono text-xs">{value}</span>;
            }
        },
        {
            accessorKey: 'backtestCount',
            header: 'Backtests',
            size: 90
        },
        {
            accessorKey: 'avgProfit',
            header: 'Avg profit',
            size: 110,
            cell: (info) => (info.getValue() as number).toFixed(2)
        },
        {
            accessorKey: 'avgPercentageGain',
            header: 'Avg % gain',
            size: 110,
            cell: (info) => `${(info.getValue() as number).toFixed(2)}%`
        },
        {
            accessorKey: 'avgAvgTradeProfit',
            header: 'Avg trade profit',
            size: 130,
            cell: (info) => (info.getValue() as number).toFixed(2)
        },
        {
            accessorKey: 'avgWinRate',
            header: 'Avg win rate',
            size: 120,
            cell: (info) => `${(info.getValue() as number).toFixed(2)}%`
        },
        {
            accessorKey: 'avgSharpeRatio',
            header: 'Avg Sharpe',
            size: 110,
            cell: (info) => (info.getValue() as number).toFixed(3)
        },
        {
            accessorKey: 'avgMedianProfitPercent',
            header: 'Avg median %',
            size: 120,
            cell: (info) => `${(info.getValue() as number).toFixed(2)}%`
        },
        {
            accessorKey: 'avgProfitPerDayPercent',
            header: 'Avg /day %',
            size: 120,
            cell: (info) => `${(info.getValue() as number).toFixed(2)}%`
        }
    ];

    const exitCandidateColumns: ColumnDef<(typeof analysisByExitCandidate)[number]>[] = [
        {
            id: 'candidateId',
            header: 'Exit Candidate',
            size: 110,
            cell: (info) => {
                return formatTradeLogicV2ExitCandidateLabel(info.row.original);
            }
        },
        {
            accessorKey: 'backtestCount',
            header: 'Backtest Count',
            size: 130
        },
        {
            id: 'avgProfit',
            header: 'Avg Profit',
            size: 120,
            cell: (info) => info.row.original.avgProfit.toFixed(2)
        },
        {
            id: 'avgPercentageGain',
            header: 'Avg % Gain',
            size: 120,
            cell: (info) => `${info.row.original.avgPercentageGain.toFixed(2)}%`
        },
        {
            id: 'avgWinRate',
            header: 'Avg Win Rate',
            size: 120,
            cell: (info) => `${info.row.original.avgWinRate.toFixed(2)}%`
        },
        {
            id: 'avgMedianProfitPercent',
            header: 'Avg Median %',
            size: 130,
            cell: (info) => `${info.row.original.avgMedianProfitPercent.toFixed(2)}%`
        },
        {
            id: 'avgProfitPerDayPercent',
            header: 'Avg /day %',
            size: 130,
            cell: (info) => `${info.row.original.avgProfitPerDayPercent.toFixed(2)}%`
        }
    ];

    const optionStrategyColumns: ColumnDef<(typeof analysisByOptionStrategy)[number]>[] = [
        {
            accessorKey: 'strategyId',
            header: 'Strategy ID',
            size: 110
        },
        {
            id: 'legsUsedIn',
            header: 'Legs Used In',
            size: 140,
            cell: (info) => info.row.original.legsUsedIn.join(', ')
        },
        {
            accessorKey: 'backtestCount',
            header: 'Backtest Count',
            size: 130
        },
        {
            id: 'avgProfit',
            header: 'Avg Profit',
            size: 120,
            cell: (info) => info.row.original.avgProfit.toFixed(2)
        },
        {
            id: 'avgPercentageGain',
            header: 'Avg % Gain',
            size: 120,
            cell: (info) => `${info.row.original.avgPercentageGain.toFixed(2)}%`
        },
        {
            id: 'avgWinRate',
            header: 'Avg Win Rate',
            size: 120,
            cell: (info) => `${info.row.original.avgWinRate.toFixed(2)}%`
        },
        {
            id: 'avgMedianProfitPercent',
            header: 'Avg Median %',
            size: 130,
            cell: (info) => `${info.row.original.avgMedianProfitPercent.toFixed(2)}%`
        },
        {
            id: 'avgProfitPerDayPercent',
            header: 'Avg /day %',
            size: 130,
            cell: (info) => `${info.row.original.avgProfitPerDayPercent.toFixed(2)}%`
        }
    ];

    if (isInitialLoading || isBacktestsLoading) {
        return (
            <Card className="mb-4">
                <div className="p-4">
                    <DataLoadingSpinner text={isInitialLoading ? 'Loading initial data...' : 'Searching backtests...'} />
                </div>
            </Card>
        );
    }

    if (backtests.length === 0) {
        return (
            <Card className="mb-4">
                <div className="p-4">
                    <div className="bg-blue-900/30 text-blue-300 px-4 py-3 rounded border border-blue-800">
                        {hasStrategyId
                            ? 'No backtests found matching the selected criteria.'
                            : 'Select a strategy and entity type, then click Search to view backtests.'}
                    </div>
                </div>
            </Card>
        );
    }

    return (
        <>
            {beforeDate && (
                <Card className="mb-4">
                    <div className="p-4">
                        <div className="bg-blue-900/30 text-blue-300 px-4 py-3 rounded border border-blue-800">
                            <div className="flex items-center">
                                <svg className="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 20 20">
                                    <path
                                        clipRule="evenodd"
                                        d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
                                        fillRule="evenodd"
                                    />
                                </svg>
                                <span>
                                    Showing stats calculated using only trades before{' '}
                                    <strong>{new Date(beforeDate).toLocaleDateString()}</strong>. Statistics may differ from the full
                                    backtest results.
                                </span>
                            </div>
                        </div>
                    </div>
                </Card>
            )}
            <div className="mb-4 flex justify-end">
                <ColumnVisibilityControl
                    columnRegistry={columnRegistry}
                    visibleColumns={visibleColumns}
                    onVisibilityChange={onVisibilityChange}
                />
            </div>
            <div className="mb-2 flex justify-end">
                <Button disabled={!hasStrategyId} size="sm" variant="secondary" onClick={() => setIsAnalysisOpen((prev) => !prev)}>
                    {isAnalysisOpen ? 'Hide best combinations' : 'Show best combinations'}
                </Button>
            </div>
            <div className="flex flex-col gap-4">
                {isAnalysisOpen && (
                    <Card>
                        <div className="p-4">
                            <h3 className="text-lg font-semibold mb-1 text-zinc-100">Best current-version parameter combinations</h3>
                            <p className="mb-3 text-xs text-zinc-500">Previous strategy versions are excluded from this analysis.</p>
                            {analysisSummary && (
                                <div className="mb-3 text-xs text-zinc-300">
                                    <div>
                                        Total backtests: <span className="font-mono">{analysisSummary.totalBacktests}</span>
                                    </div>
                                    <div>
                                        Combinations: <span className="font-mono">{analysisSummary.uniqueCombinations}</span> Exit
                                        candidates: <span className="font-mono">{analysisSummary.uniqueExitCandidates}</span> Option
                                        strategies: <span className="font-mono">{analysisSummary.uniqueOptionStrategies}</span>
                                    </div>
                                </div>
                            )}
                            {!hasStrategyId && (
                                <div className="text-sm text-zinc-400">Select a strategy and run a search to see combination analysis.</div>
                            )}
                            {hasStrategyId && (
                                <div className="mb-3 flex gap-2 text-xs">
                                    <Button
                                        size="sm"
                                        variant={activeAnalysisTab === 'combinations' ? 'primary' : 'outline-dark'}
                                        onClick={() => setActiveAnalysisTab('combinations')}
                                    >
                                        Combinations
                                    </Button>
                                    <Button
                                        size="sm"
                                        variant={activeAnalysisTab === 'exit' ? 'primary' : 'outline-dark'}
                                        onClick={() => setActiveAnalysisTab('exit')}
                                    >
                                        Exit candidates
                                    </Button>
                                    <Button
                                        size="sm"
                                        variant={activeAnalysisTab === 'options' ? 'primary' : 'outline-dark'}
                                        onClick={() => setActiveAnalysisTab('options')}
                                    >
                                        Option strategies
                                    </Button>
                                </div>
                            )}
                            {hasStrategyId && isAnalysisLoading && <DataLoadingSpinner text="Analyzing backtest combinations..." />}
                            {hasStrategyId && !isAnalysisLoading && (
                                <>
                                    {activeAnalysisTab === 'combinations' && analysisCombinations.length > 0 && (
                                        <div className="max-h-96 overflow-y-auto">
                                            <DataTable
                                                sort
                                                columns={combinationColumns}
                                                data={analysisCombinations}
                                                pagination={{ enabled: false, pageSize: 50 }}
                                                search={false}
                                            />
                                        </div>
                                    )}
                                    {activeAnalysisTab === 'exit' && analysisByExitCandidate.length > 0 && (
                                        <div className="max-h-96 overflow-y-auto">
                                            <DataTable
                                                columns={exitCandidateColumns}
                                                data={analysisByExitCandidate}
                                                pagination={{ enabled: false, pageSize: 50 }}
                                                search={false}
                                            />
                                        </div>
                                    )}
                                    {activeAnalysisTab === 'options' && analysisByOptionStrategy.length > 0 && (
                                        <div className="max-h-96 overflow-y-auto">
                                            <DataTable
                                                columns={optionStrategyColumns}
                                                data={analysisByOptionStrategy}
                                                pagination={{ enabled: false, pageSize: 50 }}
                                                search={false}
                                            />
                                        </div>
                                    )}
                                    {activeAnalysisTab === 'combinations' && analysisCombinations.length === 0 && (
                                        <div className="text-sm text-zinc-400">No valid combinations found for the current search.</div>
                                    )}
                                    {activeAnalysisTab === 'exit' && analysisByExitCandidate.length === 0 && (
                                        <div className="text-sm text-zinc-400">
                                            No exit candidate data available for the current search.
                                        </div>
                                    )}
                                    {activeAnalysisTab === 'options' && analysisByOptionStrategy.length === 0 && (
                                        <div className="text-sm text-zinc-400">
                                            No option strategy data available for the current search.
                                        </div>
                                    )}
                                </>
                            )}
                        </div>
                    </Card>
                )}
                <div className="flex-1 overflow-x-auto w-full">
                    <FilterBacktestsBulkActions selectedCount={selectedBacktestIdsCount} onDeleteSelectedClick={onDeleteSelectedClick} />
                    <div className="overflow-x-auto">
                        <DataTable
                            striped
                            columns={columnsWithSelection}
                            data={backtests}
                            pagination={{ enabled: true, pageSize: 100 }}
                            search={false}
                        />
                    </div>
                    {hasStrategyId && (
                        <div className="mt-4">
                            <Button variant="outline-dark" onClick={onFilterDeletePreview}>
                                Delete all backtests matching current search
                            </Button>
                        </div>
                    )}
                </div>
            </div>
        </>
    );
};

export default FilterBacktestsResults;
